import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { formatAttendancePolicyV2Limit } from '@/lib/attendance-policy-v2-display.js';
import { formatScheduleBreaksForDisplay } from '@/lib/meeting-block-utils.js';
import { pluralize } from '@/lib/string-utils.js';
import { formatStudentGrades } from '@/lib/student-group-utils.js';
import { formatPlainDate } from '@airreading/common';
import { CoachViewableStudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { WeeklyClassTimes } from './weekly-class-times.js';

interface CoachStudentGroupProps {
  classData: CoachViewableStudentGroupWithScheduleAndLevel;
  attendanceText?: React.ReactNode | string;
  teacherTimeZone?: string | null;
  headerActions?: React.ReactNode;
  attendancePolicyV2Enabled?: boolean;
  'data-testid'?: string;
}

const CoachStudentGroup = ({
  classData,
  attendanceText,
  teacherTimeZone,
  headerActions,
  attendancePolicyV2Enabled = false,
  'data-testid': dataTestId,
}: CoachStudentGroupProps) => {
  // Calculate duration from the first meeting block's time interval
  const firstMeetingBlock = classData.classTime.groupedMeetingBlocks?.[0];
  const duration = firstMeetingBlock
    ? Temporal.PlainTime.from(firstMeetingBlock.timeInterval.startPlainTime)
        .until(Temporal.PlainTime.from(firstMeetingBlock.timeInterval.endPlainTime))
        .total('minutes')
    : 'N/A';

  const students = classData.students.map((student) => student.coachFacingDisplayName?.trim() || '-').join(', ');

  const maxPlannedAbsences = `${classData.maxPlannedAbsences} ${pluralize(classData.maxPlannedAbsences, 'class', 'classes')}`;
  const shortNoticeSubRequestLimit = `${classData.shortNoticeSubRequestLimit} ${pluralize(classData.shortNoticeSubRequestLimit, 'class', 'classes')}`;
  const attendancePolicyV2 = attendancePolicyV2Enabled ? classData.attendancePolicyV2 : null;

  const { schedule } = classData.classTime;
  const displayTimeZone = teacherTimeZone ?? schedule.timeZone;
  const schoolBreaks = formatScheduleBreaksForDisplay(schedule.scheduleBreaks) || '-';

  // A removed coach only taught the classes before the removal took effect, so they never fall back to
  // the schedule end date.
  const lastClassDate =
    classData.teacherPortalStatus === 'removed'
      ? classData.teacherPortalLastClassPlainDate
      : (classData.teacherPortalLastClassPlainDate ?? schedule.endPlainDate);

  const baseTestId = dataTestId || `my-availability-student-group`;

  return (
    <InfoCard headerText="" contentGridClassName="grid-cols-2-auto-fix md:grid-cols-4-auto-fix [&>*]:min-w-0">
      <InfoKeyValue label="District" value={schedule.school.org.name} data-testid={`${baseTestId}-district`} />
      <InfoKeyValue label="Students" value={students} className="mr-4" data-testid={`${baseTestId}-students`} />
      <InfoKeyValue label="Grades" value={formatStudentGrades(classData.grades)} data-testid={`${baseTestId}-grades`} />
      <div className="flex min-w-0 items-start justify-between gap-2">
        <InfoKeyValue
          label="Next lesson"
          value={classData.nextLesson?.name}
          data-testid={`${baseTestId}-next-lesson`}
        />
        {headerActions ? <div className="shrink-0 self-center">{headerActions}</div> : null}
      </div>
      <WeeklyClassTimes
        classTime={classData.classTime}
        sourceTimeZone={schedule.school.org.timeZone}
        displayTimeZone={displayTimeZone}
        nextSessionAtInstant={classData.nextSessionAtInstant}
        nextSessionEndAtInstant={classData.nextSessionEndAtInstant}
        testId={`${baseTestId}-class-times`}
      />
      <InfoKeyValue label="Duration" value={`${duration} mins`} data-testid={`${baseTestId}-duration`} />
      <InfoKeyValue
        label="Last class date"
        value={lastClassDate ? formatPlainDate(lastClassDate) : '-'}
        data-testid={`${baseTestId}-last-class-date`}
      />
      <InfoKeyValue
        label="School breaks (before last class)"
        value={schoolBreaks}
        className="col-start-1"
        data-testid={`${baseTestId}-school-breaks`}
      />
      <InfoKeyValue
        label="Attendance"
        value={attendanceText}
        className="col-span-1"
        data-testid={`${baseTestId}-attendance`}
      />
      {attendancePolicyV2 ? (
        <InfoKeyValue
          label="Sub request limit"
          value={formatAttendancePolicyV2Limit(attendancePolicyV2)}
          data-testid={`${baseTestId}-sub-request-limit`}
        />
      ) : (
        <>
          <InfoKeyValue
            label="Regular sub request limit"
            value={maxPlannedAbsences}
            data-testid={`${baseTestId}-max-planned-absences`}
          />
          <InfoKeyValue
            label="Short-notice sub request limit"
            value={shortNoticeSubRequestLimit}
            data-testid={`${baseTestId}-short-notice-sub-request-limit`}
          />
        </>
      )}
    </InfoCard>
  );
};

export default CoachStudentGroup;
